(function (scope) {
  'use strict';
  const ownCode = value => /^(?:SPYRO-BULK-\d+|SMB-\d+-\d+|SMB2-\d+-[a-f0-9]{12}-\d+)$/i.test(value);
  function codes(cart) {
    const found = [];
    (cart.discount_codes || []).forEach(value => found.push(typeof value === 'string' ? value : value.code));
    function application(value) { if (value?.type === 'discount_code') found.push(value.code || value.title); }
    (cart.discount_applications || []).forEach(application);
    (cart.cart_level_discount_applications || []).forEach(application);
    (cart.items || []).forEach(item => (item.line_level_discount_allocations || []).forEach(value => application(value.discount_application)));
    return [...new Set(found.filter(value => typeof value === 'string' && value.trim()).map(value => value.trim()))];
  }
  function tierFor(config, quantity) {
    let low = 0, high = config.breaks.length;
    while (low < high) { const middle = Math.floor((low + high) / 2); if (config.breaks[middle][0] <= quantity) low = middle + 1; else high = middle; }
    return config.breaks[Math.max(0, low - 1)];
  }
  function createPricingModel(products) {
    const productFor = item => products[String(item.product_id)];
    const lines = cart => (cart.items || []).filter(item => productFor(item));
    function groups(cart) {
      const groups = new Map();
      for (const item of lines(cart)) {
        const key = String(item.product_id);
        if (!groups.has(key)) groups.set(key, {config:productFor(item), quantity:0, items:[]});
        const group = groups.get(key); group.quantity += Number(item.quantity); group.items.push(item);
      }
      return [...groups.values()];
    }
    function desiredCodes(cart) {
      return groups(cart).map(group => {
        const tier = tierFor(group.config, group.quantity);
        if (tier[1] === group.config.baseCents) return '';
        return group.config.discountPrefix ? group.config.discountPrefix + tier[0] : group.config.code === 'SPYRO' ? 'SPYRO-BULK-' + tier[0] : 'SMB-' + group.config.code + '-' + tier[0];
      }).filter(Boolean).sort();
    }
    function validate(cart) {
      const eligible = groups(cart);
      if (eligible.length && cart.currency !== 'AUD') throw Error('Please use AUD to complete this bulk-priced order.');
      for (const group of eligible) {
        if (group.config.quoteOnly || !group.config.checkoutReady) throw Error('Please request a quote for ' + (group.items[0].product_title || 'this product') + '.');
        if (!Number.isInteger(group.quantity) || group.quantity < group.config.minimum || group.quantity > group.config.maximum) throw Error('Please order between ' + group.config.minimum + ' and ' + group.config.maximum.toLocaleString('en-AU') + ' units of ' + (group.items[0].product_title || 'this product') + '.');
      }
      if (desiredCodes(cart).length > 5) throw Error('Please request a quote for orders containing more than five different bulk-priced products.');
      return cart;
    }
    function expectedLine(item, quantity) {
      const config = productFor(item);
      const fee = config.fees[String(item.variant_id || item.id)];
      if (!Number.isInteger(fee)) throw Error('Please refresh the product page before checking out.');
      return (tierFor(config, quantity)[1] + fee) * Number(item.quantity);
    }
    function verify(cart) {
      validate(cart);
      for (const group of groups(cart)) for (const item of group.items) {
        const actual = Number(item.final_line_price ?? item.line_price);
        if (!Number.isFinite(actual) || actual > expectedLine(item, group.quantity)) throw Error('We could not confirm your quantity price. Please try again or request a quote before checking out.');
      }
      return cart;
    }
    return {lines,groups,desiredCodes,validate,verify,expectedLine,codes,isOwnCode:ownCode};
  }
  if (typeof module === 'object' && module.exports) { module.exports = {createPricingModel,tierFor,codes,ownCode}; return; }
  if (!scope.document || scope.SMSpyroCheckout) return;
  const spyroElement = document.getElementById('sm-spyro-checkout-config');
  const manifestElement = document.getElementById('sm-trends-checkout-manifest');
  if (!spyroElement || !manifestElement) return;
  const spyro = JSON.parse(spyroElement.textContent), manifest = JSON.parse(manifestElement.textContent), products = {};
  products[String(spyro.productId)] = {code:'SPYRO', minimum:spyro.minimum, maximum:spyro.maximum, baseCents:spyro.baseCents, breaks:spyro.tiers.map(t => [t.minimum,t.cents]), fees:spyro.fees, checkoutReady:true};
  const productIds = new Set([String(spyro.productId), ...Object.keys(manifest)]), pendingConfigs = new Map();
  const model = createPricingModel(products), root = scope.Shopify?.routes?.root || '/', request = scope.fetch.bind(scope);
  function registerConfigurations(document) {
    document.querySelectorAll('[data-sm-bulk-product-config]').forEach(element=>{
      const id = element.dataset.productId, config = JSON.parse(element.textContent);
      if (config.layoutVersion !== 'approved-product-v1') throw Error('Product pricing needs verification before checkout.');
      products[id]=config; productIds.add(id);
    });
  }
  registerConfigurations(document);
  let queue = Promise.resolve(), checkoutPending = false;
  async function configFor(id) {
    id = String(id);
    if (products[id]) return products[id];
    if (!manifest[id]) return null;
    if (!pendingConfigs.has(id)) pendingConfigs.set(id, request(manifest[id], {credentials:'same-origin'}).then(async response => {
      if (!response.ok) throw Error('Quantity pricing is temporarily unavailable. Please try again or request a quote.');
      const config = await response.json(); products[id] = config; return config;
    }).catch(error => {pendingConfigs.delete(id); throw error;}));
    return pendingConfigs.get(id);
  }
  async function loadCartProducts(cart) {
    const response = await request(root + 'cart?sections=sm-bulk-cart-config', {credentials:'same-origin',cache:'no-store'});
    if (!response.ok) throw Error('Current quantity pricing could not be checked. Please try again.');
    const sections = await response.json();
    if (typeof sections['sm-bulk-cart-config'] !== 'string') throw Error('Current quantity pricing could not be checked. Please refresh the page.');
    registerConfigurations(new DOMParser().parseFromString(sections['sm-bulk-cart-config'],'text/html'));
    const ids = [...new Set((cart.items || []).map(item => String(item.product_id)).filter(id => productIds.has(id)))];
    await Promise.all(ids.map(configFor));
  }
  async function cartRequest(path, body) {
    const response = await request(root + path, body ? {method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(body),credentials:'same-origin'} : {credentials:'same-origin',cache:'no-store',headers:{'Accept':'application/json'}});
    const value = await response.json();
    if (!response.ok) throw Error(value.description || 'Your cart could not be updated. Please try again.');
    return value;
  }
  function updateHeaderCount(cart) {
    const count = Number(cart.item_count ?? (cart.items || []).reduce((sum,item) => sum + Number(item.quantity),0));
    document.querySelectorAll('.sm-cart-count').forEach(element => element.textContent = '(' + count + ')');
  }
  function showError(error, near) {
    let box = document.getElementById('sm-spyro-checkout-error');
    if (!box) { box = document.createElement('p'); box.id = 'sm-spyro-checkout-error'; box.className = 'sm-spyro-checkout-error'; box.setAttribute('role','alert'); (near || document.querySelector('main') || document.body).prepend(box); }
    box.textContent = error.message || 'Your cart could not be updated. Please try again.'; box.hidden = false;
  }
  async function updatePricing() {
    let cart = await cartRequest('cart.js');
    await loadCartProducts(cart);
    model.validate(cart);
    const desired = model.desiredCodes(cart), current = model.codes(cart), currentOwn = current.filter(ownCode).sort();
    let mustApply = JSON.stringify(desired) !== JSON.stringify(currentOwn);
    if (!mustApply) { try { model.verify(cart); } catch (_) { mustApply = true; } }
    if (mustApply) { cart = await cartRequest('cart/update.js',{discount:[...current.filter(code => !ownCode(code)),...desired].join(',')}); model.verify(cart); }
    else model.verify(cart);
    const error = document.getElementById('sm-spyro-checkout-error'); if (error) error.hidden = true;
    updateHeaderCount(cart); return cart;
  }
  function sync() { const job = queue.then(updatePricing,updatePricing); queue = job.catch(() => {}); return job; }
  function needsSync(cart) { return (cart.items || []).some(item => productIds.has(String(item.product_id))) || codes(cart).some(ownCode); }
  async function waitForCartEdit() {
    const started = Date.now();
    while (document.querySelector('[data-cart-items].disabled,.cart-items-wrapper.disabled')) { if (Date.now() - started > 12000) throw Error('Your cart is still updating. Please try checkout again in a moment.'); await new Promise(resolve => setTimeout(resolve,100)); }
  }
  async function checkout(control) {
    if (checkoutPending) return;
    const form = control?.form || document.getElementById(control?.getAttribute('form')) || control?.closest('form');
    if (form && !form.reportValidity()) return;
    checkoutPending = true; control?.setAttribute('aria-busy','true');
    try { await waitForCartEdit(); await sync(); location.assign(root + 'checkout'); }
    catch (error) { showError(error); checkoutPending = false; control?.removeAttribute('aria-busy'); }
  }
  async function addToCart(control) {
    const formId = control.getAttribute('form') || control.form?.id;
    const component = [...document.querySelectorAll('sm-spyro-pricing,sm-trends-pricing')].find(element => element.dataset.formId === formId);
    if (!component || control.disabled) return;
    if (control.dataset.smBulkAdded === 'true') { location.assign(root + 'cart'); return; }
    const input = component.matches('sm-spyro-pricing') ? component.qtyInput() : component.querySelector('[data-quantity]');
    if (!input?.reportValidity() || component.loading) return;
    if (component.matches('sm-spyro-pricing')) component.update(); else component.updatePrice();
    const form = document.getElementById(formId), variant = form.querySelector('[name="id"]')?.value;
    if (!variant || !component.querySelector('[data-error]').hidden) return;
    const properties = {'_SM Bulk Pricing':'v1'};
    const estimateName = component.querySelector('[data-price-property]')?.name;
    for (const [name,value] of new FormData(form)) { const match = name.match(/^properties\[(.+)\]$/); if (name !== estimateName && match && typeof value === 'string' && value) properties[match[1]] = value; }
    const quantity = Number(input.value);
    const label = control.textContent; let added = false;
    control.disabled = true; control.setAttribute('aria-busy','true'); control.textContent = 'Adding to cart…';
    try {
      const config = await configFor(component.dataset.productId || spyro.productId);
      if (!config?.checkoutReady) throw Error('Please request a quote for this product.');
      await cartRequest('cart/add.js',{items:[{id:Number(variant),quantity,properties}]}); added = true; await sync(); location.assign(root + 'cart');
    }
    catch (error) { showError(error,component); control.textContent = added ? 'View cart' : label; control.dataset.smBulkAdded = String(added); control.disabled = false; control.removeAttribute('aria-busy'); }
  }
  scope.SMSpyroCheckout = scope.SMBulkCheckout = {sync,checkout,showError,updateHeaderCount,needsSync};
  document.addEventListener('click',event => {
    const add = event.target.closest('[data-sm-cart-action],[data-sm-spyro-add]');
    if (add) { event.preventDefault(); event.stopImmediatePropagation(); addToCart(add).catch(error => showError(error)); return; }
    const control = event.target.closest('[data-sm-online-checkout],[name="checkout"],a[href]'); if (!control) return;
    const link = control.matches('a[href]') ? new URL(control.href,location.href) : null;
    const checkoutLink = link?.origin === location.origin && link.pathname.replace(/\/$/,'').endsWith('/checkout');
    if (!checkoutLink && !control.matches('[data-sm-online-checkout],[name="checkout"]')) return;
    event.preventDefault(); event.stopImmediatePropagation(); checkout(control);
  },true);
  document.addEventListener('submit',event => { if (!event.submitter?.matches('[name="checkout"]')) return; event.preventDefault(); event.stopImmediatePropagation(); checkout(event.submitter); },true);
  function ready() {
    if (!location.pathname.replace(/\/$/,'').endsWith('/cart')) return;
    sync().then(cart => { if (needsSync(cart) && typeof scope.cartPageUpdate === 'function') scope.cartPageUpdate(cart); }).catch(error => showError(error));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',ready,{once:true}); else ready();
})(typeof window === 'object' ? window : globalThis);

